import { Card, Divider, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getOperationsData, saveOperationsForm } from '../../../../../../api/transitions';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import { trimData } from '../../../../../../utils/formatText';
import SkeletonBlock from '../../../SkeletonBlock';
import { formCardStyle } from '../../../TransitionStyles';
import { flattenTransitionSubmitData } from '../../../formDataUtils';
import { OPERATIONS } from '../../../formQuestionsEnum';
import CorporateComplianceForm from './CorporateComplianceForm';
import DocumentUploadsForm from './DocumentUploadsForm';
import MedicalDirectorForm from './MedicalDirectorForm';
import StaffForm from './StaffForm';

const OperationsView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);

  const operationsPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.operationsFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getOperationsData(id, facilityId ?? 0)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(OPERATIONS).map(([, v]) => [v.id, v.fieldName]));

    const mappedFields = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [data]);

  const submitForm = async (allData) => {
    const flattenData = flattenTransitionSubmitData(allData);
    const result = trimData(flattenData);

    await saveOperationsForm({ id, sellerFacilityId: facilityId, ...result })
      .then((resp) => {
        enqueueSnackbar('Operations data saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save Operations data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <>
      <StaffForm permissions={operationsPermissions} />
      <DocumentUploadsForm permissions={operationsPermissions} />
      <CorporateComplianceForm permissions={operationsPermissions} />
      <MedicalDirectorForm permissions={operationsPermissions} />
    </>
  );
});

export default OperationsView;

OperationsView.propTypes = {
  setFormData: PropTypes.func,
};
